import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Pencil,
  Eye,
  ArrowLeft,
  Loader2,
  Save,
  Send,
  Mail,
  QrCode,
  Copy,
  Trash2,
  ChevronUp,
  ChevronDown,
  CheckCircle,
  Brain,
  ClipboardList,
  AlignLeft,
  Hash,
  Type,
  Star,
  ToggleLeft,
  X,
  Download,
  RefreshCw,
} from "lucide-react";
import { AUTH_URL } from "../../constants";
import { useAuth } from "../../contexts/AuthContext";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SURVEY_QTYPES = [
  { id: "short_text", label: "Short text", icon: Type },
  { id: "long_text", label: "Paragraph", icon: AlignLeft },
  { id: "single_select", label: "Single choice", icon: ClipboardList },
  { id: "multi_select", label: "Multiple choice", icon: ClipboardList },
  { id: "boolean", label: "Yes / No", icon: ToggleLeft },
  { id: "scale", label: "Scale 1–5", icon: Star },
  { id: "number", label: "Number", icon: Hash },
];
const SURVEY_DOC_TYPES = ["position", "faq", "event", "legislative"];
const STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-600",
  published: "bg-green-100 text-green-700",
  closed: "bg-red-100 text-red-600",
};

let _sqIdx = 0;
const uid = () => `q${++_sqIdx}_${Date.now()}`;
const blankQuestion = () => ({
  id: uid(),
  type: "short_text",
  label: "",
  topic: "",
  required: false,
  options: [],
  kbInclude: true,
  doc_type: "position",
});

// ---------------------------------------------------------------------------
// useSurveyFetch — thin wrapper over fetch using admin JWT
// ---------------------------------------------------------------------------

function useSurveyFetch(token) {
  return useCallback(
    async (path, opts = {}) => {
      try {
        const r = await fetch(`${AUTH_URL}${path}`, {
          ...opts,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            ...(opts.headers || {}),
          },
        });
        return await r.json();
      } catch {
        return { error: "Network error" };
      }
    },
    [token],
  );
}

// ---------------------------------------------------------------------------
// SurveyList
// ---------------------------------------------------------------------------

function SurveyList({ facilityId, apiFetch, onEdit, onResponses, onNew }) {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const d = await apiFetch(`/surveys?facility_id=${facilityId}`);
    setSurveys(Array.isArray(d) ? d : []);
    setLoading(false);
  }, [facilityId, apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Surveys</h2>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            onClick={onNew}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus size={15} />
            New Survey
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={22} className="animate-spin text-blue-500" />
        </div>
      ) : surveys.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <ClipboardList size={36} className="mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-gray-500">No surveys yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Create a survey to collect candidate or constituent responses.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[
                  "Title",
                  "Audience",
                  "Status",
                  "Responses",
                  "KB Questions",
                  "Created",
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {surveys.map((s) => {
                // Counted by the list query. Reading it off s.schema meant
                // this always showed 0, since the list never carried schema.
                const kbCount = s.kb_question_count ?? 0;
                return (
                  <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 font-medium text-gray-800">
                      {s.title || (
                        <span className="text-gray-400 italic">Untitled</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-600 capitalize">
                      {s.audience}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                          STATUS_BADGE[s.status] || "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {s.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {s.response_count ?? 0}
                    </td>
                    <td className="py-3 px-4">
                      {kbCount > 0 ? (
                        <span className="flex items-center gap-1 text-blue-600 text-xs font-medium">
                          <Brain size={12} />
                          {kbCount}
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-xs">
                      {new Date(s.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => onEdit(s)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => onResponses(s)}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                          title="View Responses"
                        >
                          <Eye size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SurveyEditor
// ---------------------------------------------------------------------------

function SurveyEditor({ facilityId, surveyId, apiFetch, onBack, onSaved }) {
  const [survey, setSurvey] = useState(null);
  const [localTitle, setLocalTitle] = useState("");
  const [localAudience, setLocalAudience] = useState("candidate");
  const [localQuestions, setLocalQuestions] = useState([blankQuestion()]);
  const [expandedQ, setExpandedQ] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(!!surveyId);
  const [emailsText, setEmailsText] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const [showKbMap, setShowKbMap] = useState(false);
  const [err, setErr] = useState("");

  const isLocked = survey?.status === "closed";
  const isDraft = !survey || survey.status === "draft";
  const isPublished = survey?.status === "published";

  useEffect(() => {
    if (!surveyId) return;
    apiFetch(`/surveys/${surveyId}`).then((d) => {
      if (!d || d.error) return;
      setSurvey(d);
      setLocalTitle(d.title || "");
      setLocalAudience(d.audience || "candidate");
      setLocalQuestions(d.schema?.length ? d.schema : [blankQuestion()]);
      setLoading(false);
    });
  }, [surveyId, apiFetch]);

  const handleSave = async () => {
    setSaving(true);
    setErr("");
    const body = {
      title: localTitle,
      audience: localAudience,
      schema: localQuestions,
    };
    const res = await apiFetch(surveyId ? `/surveys/${surveyId}` : "/surveys", {
      method: surveyId ? "PUT" : "POST",
      body: JSON.stringify(
        surveyId ? body : { ...body, facility_id: facilityId },
      ),
    });
    setSaving(false);
    if (res?.error) {
      setErr(res.error);
    } else {
      setSurvey(res);
      if (!surveyId) onSaved?.(res);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    setErr("");
    const emails = emailsText
      .split(/[\n,]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    const res = await apiFetch(`/surveys/${survey.id}/publish`, {
      method: "POST",
      body: JSON.stringify(localAudience === "candidate" ? { emails } : {}),
    });
    setPublishing(false);
    if (res?.error) {
      setErr(res.error);
    } else {
      setSurvey((p) => ({ ...p, status: "published" }));
      if (localAudience === "candidate") {
        setInviteResult(res);
        setEmailsText("");
      }
      // Constituent and user surveys have no link to hand back: constituent
      // responses come from single-use tokens minted in FieldIQ, and user
      // surveys surface automatically. Publishing is the distribution step.
    }
  };

  const handleInvite = async () => {
    setPublishing(true);
    setErr("");
    const emails = emailsText
      .split(/[\n,]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    const res = await apiFetch(`/surveys/${survey.id}/invite`, {
      method: "POST",
      body: JSON.stringify({ emails }),
    });
    setPublishing(false);
    if (res?.error) {
      setErr(res.error);
    } else {
      setInviteResult(res);
      setEmailsText("");
    }
  };

  const toggleQ = (id) =>
    setExpandedQ((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const updateQ = (id, patch) =>
    setLocalQuestions((qs) =>
      qs.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    );

  const removeQ = (id) =>
    setLocalQuestions((qs) => qs.filter((q) => q.id !== id));

  const addQ = () => {
    const q = blankQuestion();
    setLocalQuestions((qs) => [...qs, q]);
    setExpandedQ((p) => new Set([...p, q.id]));
  };

  const moveQ = (idx, dir) =>
    setLocalQuestions((qs) => {
      const n = [...qs];
      const t = idx + dir;
      if (t < 0 || t >= n.length) return n;
      [n[idx], n[t]] = [n[t], n[idx]];
      return n;
    });

  const kbPreview = useMemo(
    () =>
      localAudience !== "candidate"
        ? []
        : localQuestions
            .filter((q) => q.kbInclude && q.label)
            .map((q) => ({
              label: q.label,
              doc_type: q.doc_type || "position",
            })),
    [localQuestions, localAudience],
  );

  if (loading)
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={22} className="animate-spin text-blue-500" />
      </div>
    );

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <input
          className="flex-1 text-lg font-semibold text-gray-800 border-0 outline-none bg-transparent placeholder-gray-300"
          placeholder="Survey title…"
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          disabled={isLocked}
        />
        {localAudience === "candidate" && (
          <button
            onClick={() => setShowKbMap((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              showKbMap
                ? "bg-blue-100 text-blue-700"
                : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            <Brain size={14} />
            KB Map
          </button>
        )}
        <button
          onClick={handleSave}
          disabled={saving || isLocked}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          {saving ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Save size={14} />
          )}
          Save
        </button>
        {isDraft && survey?.id && (
          <button
            onClick={handlePublish}
            disabled={publishing}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {publishing ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Send size={14} />
            )}
            Publish
          </button>
        )}
        {survey?.status && (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
              STATUS_BADGE[survey.status] || "bg-gray-100 text-gray-600"
            }`}
          >
            {survey.status}
          </span>
        )}
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">
          {err}
        </div>
      )}

      <div className={`flex gap-4 ${showKbMap ? "items-start" : ""}`}>
        <div className="flex-1 space-y-4 min-w-0">
          {/* Audience */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Audience</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                {
                  id: "candidate",
                  label: "Candidate",
                  hint: "Email-invited · approved answers can sync to KB",
                },
                {
                  id: "constituent",
                  label: "Constituent",
                  hint: "Public QR / link · canvasser-attributed",
                },
                {
                  id: "user",
                  label: "User",
                  hint: "Your FieldIQ users · editable while published",
                },
              ].map(({ id, label, hint }) => (
                <button
                  key={id}
                  onClick={() =>
                    !isLocked && !isPublished && setLocalAudience(id)
                  }
                  disabled={isLocked || isPublished}
                  className={`p-3 rounded-lg border-2 text-left transition-colors disabled:cursor-not-allowed ${
                    localAudience === id
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="font-medium text-gray-800">{label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Questions */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <p className="font-medium text-gray-700">Questions</p>
              {!isLocked && (
                <button
                  onClick={addQ}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  <Plus size={14} />
                  Add question
                </button>
              )}
            </div>
            {localQuestions.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-sm">
                No questions — add one above.
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {localQuestions.map((q, idx) => {
                  const isExp = expandedQ.has(q.id);
                  const QIcon =
                    SURVEY_QTYPES.find((t) => t.id === q.type)?.icon || Type;
                  return (
                    <div key={q.id} className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => moveQ(idx, -1)}
                            disabled={idx === 0 || isLocked}
                            className="text-gray-300 hover:text-gray-500 disabled:opacity-20"
                          >
                            <ChevronUp size={13} />
                          </button>
                          <button
                            onClick={() => moveQ(idx, 1)}
                            disabled={
                              idx === localQuestions.length - 1 || isLocked
                            }
                            className="text-gray-300 hover:text-gray-500 disabled:opacity-20"
                          >
                            <ChevronDown size={13} />
                          </button>
                        </div>
                        <QIcon
                          size={14}
                          className="text-gray-400 flex-shrink-0"
                        />
                        <span className="flex-1 text-sm text-gray-700 truncate">
                          {q.label || (
                            <span className="text-gray-400 italic">
                              Question {idx + 1}
                            </span>
                          )}
                        </span>
                        {q.kbInclude && localAudience === "candidate" && (
                          <span className="flex items-center gap-0.5 text-xs text-blue-500 font-medium flex-shrink-0">
                            <Brain size={11} />
                            KB
                          </span>
                        )}
                        {q.required && (
                          <span className="text-xs text-red-500 font-medium flex-shrink-0">
                            Required
                          </span>
                        )}
                        {!isLocked && (
                          <button
                            onClick={() => removeQ(q.id)}
                            className="p-1 text-gray-300 hover:text-red-400 rounded"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                        <button
                          onClick={() => toggleQ(q.id)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                        >
                          {isExp ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )}
                        </button>
                      </div>
                      {isExp && (
                        <div className="mt-3 space-y-3 pl-10">
                          <textarea
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-50"
                            rows={2}
                            placeholder="Question label…"
                            value={q.label}
                            onChange={(e) =>
                              updateQ(q.id, { label: e.target.value })
                            }
                            disabled={isLocked}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Type
                              </label>
                              <select
                                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-50"
                                value={q.type}
                                onChange={(e) =>
                                  updateQ(q.id, {
                                    type: e.target.value,
                                    options: [
                                      "single_select",
                                      "multi_select",
                                    ].includes(e.target.value)
                                      ? q.options?.length
                                        ? q.options
                                        : [""]
                                      : [],
                                  })
                                }
                                disabled={isLocked}
                              >
                                {SURVEY_QTYPES.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Topic
                              </label>
                              <input
                                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-50"
                                placeholder="e.g. Economy"
                                value={q.topic}
                                onChange={(e) =>
                                  updateQ(q.id, { topic: e.target.value })
                                }
                                disabled={isLocked}
                              />
                            </div>
                          </div>
                          {["single_select", "multi_select"].includes(
                            q.type,
                          ) && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Options
                              </label>
                              {(q.options || []).map((opt, oi) => (
                                <div
                                  key={oi}
                                  className="flex items-center gap-2 mb-1"
                                >
                                  <input
                                    className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-50"
                                    value={opt}
                                    placeholder={`Option ${oi + 1}`}
                                    onChange={(e) => {
                                      const opts = [...q.options];
                                      opts[oi] = e.target.value;
                                      updateQ(q.id, { options: opts });
                                    }}
                                    disabled={isLocked}
                                  />
                                  {!isLocked && (
                                    <button
                                      onClick={() =>
                                        updateQ(q.id, {
                                          options: q.options.filter(
                                            (_, i) => i !== oi,
                                          ),
                                        })
                                      }
                                      className="text-gray-300 hover:text-red-400"
                                    >
                                      <X size={13} />
                                    </button>
                                  )}
                                </div>
                              ))}
                              {!isLocked && (
                                <button
                                  onClick={() =>
                                    updateQ(q.id, {
                                      options: [...(q.options || []), ""],
                                    })
                                  }
                                  className="text-blue-600 text-xs hover:text-blue-700 mt-1"
                                >
                                  + Add option
                                </button>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-4 flex-wrap">
                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={q.required}
                                onChange={(e) =>
                                  updateQ(q.id, { required: e.target.checked })
                                }
                                disabled={isLocked}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-300"
                              />
                              Required
                            </label>
                            {localAudience === "candidate" && (
                              <>
                                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={q.kbInclude}
                                    onChange={(e) =>
                                      updateQ(q.id, {
                                        kbInclude: e.target.checked,
                                      })
                                    }
                                    disabled={isLocked}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-300"
                                  />
                                  <Brain size={13} className="text-blue-500" />
                                  Add to KB on approval
                                </label>
                                {q.kbInclude && (
                                  <select
                                    className="border border-gray-200 rounded-lg px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:bg-gray-50"
                                    value={q.doc_type || "position"}
                                    onChange={(e) =>
                                      updateQ(q.id, {
                                        doc_type: e.target.value,
                                      })
                                    }
                                    disabled={isLocked}
                                  >
                                    {SURVEY_DOC_TYPES.map((dt) => (
                                      <option key={dt} value={dt}>
                                        {dt}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Distribution */}
          {survey?.id && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="font-medium text-gray-700 mb-3">Distribution</p>
              {localAudience === "candidate" ? (
                <div className="space-y-3">
                  <label className="block text-xs font-medium text-gray-500">
                    Candidate emails — one per line or comma-separated
                  </label>
                  <textarea
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
                    rows={4}
                    placeholder={"candidate@example.com\nanother@example.com"}
                    value={emailsText}
                    onChange={(e) => setEmailsText(e.target.value)}
                    disabled={isLocked}
                  />
                  {isDraft && (
                    <p className="text-xs text-gray-400">
                      Fill emails above, then click Publish to mint invitation
                      links.
                    </p>
                  )}
                  {isPublished && !isLocked && (
                    <button
                      onClick={handleInvite}
                      disabled={publishing || !emailsText.trim()}
                      className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {publishing ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Mail size={14} />
                      )}
                      Send to additional candidates
                    </button>
                  )}
                  {inviteResult?.links?.length > 0 && (
                    <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
                      <p className="text-xs font-medium text-gray-500">
                        Invitation links
                      </p>
                      {inviteResult.links.map((l) => (
                        <div
                          key={l.email}
                          className="flex items-center gap-2 text-sm"
                        >
                          <Mail
                            size={13}
                            className="text-gray-400 flex-shrink-0"
                          />
                          <span className="text-gray-600 w-40 truncate text-xs">
                            {l.email}
                          </span>
                          <span className="text-gray-400 truncate flex-1 font-mono text-xs">
                            {l.link}
                          </span>
                          <button
                            onClick={() =>
                              navigator.clipboard.writeText(l.link)
                            }
                            className="text-gray-400 hover:text-blue-600 flex-shrink-0"
                          >
                            <Copy size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : localAudience === "user" ? (
                <div className="text-sm text-gray-600 space-y-1.5">
                  <p>
                    No links to send. Once published, this survey appears as a
                    red alert on the FieldIQ <strong>Today</strong> screen for
                    every user with access to this facility.
                  </p>
                  <p className="text-xs text-gray-400">
                    Respondents can keep editing their answers while the survey
                    stays published.
                  </p>
                </div>
              ) : (
                <div className="flex items-start gap-2.5 text-sm text-gray-600">
                  <QrCode
                    size={15}
                    className="text-blue-500 flex-shrink-0 mt-0.5"
                  />
                  <div className="space-y-1.5">
                    <p>
                      Nothing to copy. Constituent surveys are{" "}
                      <strong>single use</strong> — a canvasser generates a
                      one-time QR code or shareable link from FieldIQ at the
                      door.
                    </p>
                    <p className="text-xs text-gray-400">
                      Each link works once, expires after 7 days, and carries
                      the canvasser and issue location, so responses are
                      attributed automatically.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* KB Map panel */}
        {showKbMap && localAudience === "candidate" && (
          <div className="w-72 flex-shrink-0 bg-white rounded-xl border border-blue-200 p-4 sticky top-4">
            <p className="font-medium text-gray-700 mb-1 flex items-center gap-1.5">
              <Brain size={14} className="text-blue-500" />
              KB rows on approval
            </p>
            <p className="text-xs text-gray-400 mb-3">
              One row per question with "Add to KB" checked.
            </p>
            {kbPreview.length === 0 ? (
              <p className="text-sm text-gray-400 italic">
                No KB-tagged questions yet.
              </p>
            ) : (
              <div className="space-y-2">
                {kbPreview.map((p, i) => (
                  <div
                    key={i}
                    className="border border-gray-100 rounded-lg p-2 text-xs"
                  >
                    <div className="font-medium text-gray-700 truncate">
                      {p.label}
                    </div>
                    <div className="text-gray-400 mt-0.5">
                      <span className="capitalize">{p.doc_type}</span>
                      {" · "}
                      <span className="font-mono">pending_review</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SurveyResponses
// ---------------------------------------------------------------------------

const parseAnswers = (raw) => {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }
  return raw;
};

const formatAnswer = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (Array.isArray(value)) return value.join("; ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const SURVEY_IDENTITY_COLUMNS = {
  candidate: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "office", label: "Office" },
  ],
  constituent: [{ key: "captured_by", label: "Captured by" }],
  user: [
    { key: "respondent_name", label: "Name" },
    { key: "respondent_email", label: "Email" },
  ],
};

const slugify = (t) =>
  String(t)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60) || "survey";

const csvCell = (v) => {
  const str = v === undefined || v === null ? "" : String(v);
  // Quote anything that could otherwise break the row, and double any quote.
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const downloadCsv = (filename, rows) => {
  const body = rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
  // BOM so Excel reads UTF-8 names rather than mangling them.
  const blob = new Blob(["\uFEFF" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

function SurveyResponses({ survey, apiFetch, onBack }) {
  const [responses, setResponses] = useState([]);
  const [schema, setSchema] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedR, setExpandedR] = useState(new Set());
  const [approving, setApproving] = useState(new Set());

  const isCandidate = survey.audience === "candidate";

  const load = useCallback(async () => {
    setLoading(true);
    const d = await apiFetch(`/surveys/${survey.id}/responses`);
    // The endpoint returns { responses, schema, ... }. Reading it as an array
    // meant this screen showed "No responses yet" no matter what.
    setResponses(Array.isArray(d?.responses) ? d.responses : []);
    // Schema is not on the survey list row, so without this every answer was
    // labelled with its raw question id and the KB tags never showed.
    setSchema(Array.isArray(d?.schema) ? d.schema : []);
    setLoading(false);
  }, [survey.id, apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleR = (id) =>
    setExpandedR((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const handleApprove = async (rid) => {
    setApproving((p) => new Set([...p, rid]));
    const res = await apiFetch(
      `/surveys/${survey.id}/responses/${rid}/approve`,
      {
        method: "POST",
      },
    );
    setApproving((p) => {
      const n = new Set(p);
      n.delete(rid);
      return n;
    });
    if (!res?.error)
      setResponses((p) =>
        p.map((r) => (r.id === rid ? { ...r, approved: true } : r)),
      );
  };

  // Questions in the order they were asked, plus any answer whose question has
  // since been removed, so nothing is silently dropped.
  const questionColumns = useMemo(() => {
    const cols = schema
      .filter((q) => q?.id)
      .map((q) => ({
        id: q.id,
        label: q.label?.trim() || q.id,
        kbInclude: !!q.kbInclude,
      }));
    const known = new Set(cols.map((c) => c.id));
    responses.forEach((r) => {
      Object.keys(parseAnswers(r.answers)).forEach((qid) => {
        if (!known.has(qid)) {
          known.add(qid);
          cols.push({ id: qid, label: qid, orphan: true });
        }
      });
    });
    return cols;
  }, [schema, responses]);

  const exportCsv = () => {
    const idCols = SURVEY_IDENTITY_COLUMNS[survey.audience] || [];
    const header = [
      "Submitted",
      ...idCols.map((c) => c.label),
      ...questionColumns.map((c) => c.label),
      ...(isCandidate ? ["Approved"] : []),
    ];
    const rows = responses.map((r) => {
      const answers = parseAnswers(r.answers);
      return [
        r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "",
        ...idCols.map((c) => r[c.key] ?? ""),
        ...questionColumns.map((c) => formatAnswer(answers[c.id])),
        ...(isCandidate ? [r.approved ? "yes" : "no"] : []),
      ];
    });
    downloadCsv(
      `${slugify(survey.title || "survey")}-responses-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
      [header, ...rows],
    );
  };

  const approvedCount = responses.filter((r) => r.approved).length;
  const pendingCount = responses.filter(
    (r) => !r.approved && isCandidate,
  ).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-800 truncate">
            {survey.title || "Untitled survey"} — Responses
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {responses.length} response{responses.length !== 1 ? "s" : ""}
            {isCandidate && pendingCount > 0 && (
              <span className="text-amber-600">
                {" · "}
                {pendingCount} pending approval
              </span>
            )}
            {isCandidate && approvedCount > 0 && (
              <span className="text-green-600">
                {" · "}
                {approvedCount} approved & queued for KB
              </span>
            )}
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={responses.length === 0}
          className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          title="Download every response as CSV"
        >
          <Download size={14} />
          Export CSV
        </button>
        <button
          onClick={load}
          disabled={loading}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={22} className="animate-spin text-blue-500" />
        </div>
      ) : responses.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <ClipboardList size={36} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 font-medium">No responses yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {responses.map((r) => {
            const isExp = expandedR.has(r.id);
            const answers = parseAnswers(r.answers);
            const kbQuestions = questionColumns.filter((q) => q.kbInclude);
            return (
              <div
                key={r.id}
                className="bg-white rounded-xl border border-gray-200"
              >
                <div className="p-4 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    {isCandidate ? (
                      <>
                        <p className="font-medium text-gray-800 truncate">
                          {r.name || r.email || `Response #${r.id}`}
                        </p>
                        {r.email && r.name && (
                          <p className="text-xs text-gray-400">{r.email}</p>
                        )}
                        {r.office && (
                          <p className="text-xs text-gray-500">{r.office}</p>
                        )}
                      </>
                    ) : (
                      <p className="font-medium text-gray-700">
                        Response #{r.id}
                        {r.captured_by && (
                          <span className="text-gray-400 font-normal">
                            {" "}
                            · PC {r.captured_by}
                          </span>
                        )}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      {new Date(r.submitted_at).toLocaleString()}
                    </p>
                    {isCandidate && kbQuestions.length > 0 && (
                      <p className="text-xs text-blue-500 mt-0.5 flex items-center gap-1">
                        <Brain size={11} />
                        {kbQuestions.length} question
                        {kbQuestions.length !== 1 ? "s" : ""} tagged for KB
                        {r.approved && " · synced to KB Review Queue"}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.approved ? (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                        <CheckCircle size={11} />
                        Approved
                      </span>
                    ) : (
                      isCandidate && (
                        <button
                          onClick={() => handleApprove(r.id)}
                          disabled={approving.has(r.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          {approving.has(r.id) ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <Brain size={13} />
                          )}
                          Approve &amp; Add to KB
                        </button>
                      )
                    )}
                    <button
                      onClick={() => toggleR(r.id)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md"
                    >
                      {isExp ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </button>
                  </div>
                </div>
                {isExp && (
                  <div className="border-t border-gray-100 divide-y divide-gray-50">
                    {questionColumns.map((col) => {
                      const display = formatAnswer(answers[col.id]);
                      return (
                        <div key={col.id} className="px-4 py-3">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-xs font-medium text-gray-500 mb-1">
                              {col.label}
                              {col.orphan && (
                                <span className="ml-1.5 text-gray-400 font-normal">
                                  (question since removed)
                                </span>
                              )}
                            </p>
                            {col.kbInclude && (
                              <span className="text-xs text-blue-400 flex items-center gap-0.5 flex-shrink-0">
                                <Brain size={10} />
                                KB
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {display || (
                              <span className="italic text-gray-400">
                                Not answered
                              </span>
                            )}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SurveysAdmin — orchestrator
// ---------------------------------------------------------------------------

export default function SurveysAdmin() {
  const { token } = useAuth();
  const apiFetch = useSurveyFetch(token);

  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [view, setView] = useState("list");
  const [activeSurvey, setActiveSurvey] = useState(null);

  useEffect(() => {
    fetch(`${AUTH_URL}/admin/facilities`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : d.facilities || [];
        setFacilities(list);
        if (list.length > 0) setFacilityId(list[0].facility_id);
      })
      .catch(() => {});
  }, [token]);

  const handleNew = async () => {
    const res = await apiFetch("/surveys", {
      method: "POST",
      body: JSON.stringify({
        facility_id: facilityId,
        title: "",
        audience: "candidate",
        schema: [],
      }),
    });
    if (res?.id) {
      setActiveSurvey(res);
      setView("editor");
    }
  };

  const goBack = () => {
    setActiveSurvey(null);
    setView("list");
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Surveys</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Candidate and constituent surveys · approved responses sync to KB
            Review Queue
          </p>
        </div>
        {view === "list" && facilities.length > 1 && (
          <select
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {facilities.map((f) => (
              <option key={f.facility_id} value={f.facility_id}>
                {f.display_name || f.facility_id}
              </option>
            ))}
          </select>
        )}
      </div>

      {view === "editor" && (
        <SurveyEditor
          facilityId={facilityId}
          surveyId={activeSurvey?.id}
          apiFetch={apiFetch}
          onBack={goBack}
          onSaved={(s) => setActiveSurvey(s)}
        />
      )}

      {view === "responses" && activeSurvey && (
        <SurveyResponses
          survey={activeSurvey}
          apiFetch={apiFetch}
          onBack={goBack}
        />
      )}

      {view === "list" && facilityId && (
        <SurveyList
          facilityId={facilityId}
          apiFetch={apiFetch}
          onNew={handleNew}
          onEdit={(s) => {
            setActiveSurvey(s);
            setView("editor");
          }}
          onResponses={(s) => {
            setActiveSurvey(s);
            setView("responses");
          }}
        />
      )}
    </div>
  );
}
